/**
 * Dashboard alerts zone — shows critical/warning operational alerts.
 * No section title: the red/yellow/green coloring is self-evident.
 */

import { Link } from "react-router-dom";
import { t } from "../i18n";

// ─── Types ───

type AlertSeverity = "critical" | "warning";

type ActiveAlert = {
  readonly id: string;
  readonly severity: AlertSeverity;
  readonly message: string;
  readonly detail: string;
  readonly link_to: string;
  readonly link_label: string;
};

type AlertsSectionProps = {
  readonly alerts: ReadonlyArray<ActiveAlert>;
};

// ─── Sub-components ───

function SeverityDot({
  severity,
}: {
  severity: AlertSeverity;
}): React.JSX.Element {
  const bg = severity === "critical" ? "#dc2626" : "#f59e0b";
  return (
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: bg,
        display: "inline-block",
        flexShrink: 0,
      }}
    />
  );
}

function AlertRow({ alert }: { alert: ActiveAlert }): React.JSX.Element {
  const isCritical = alert.severity === "critical";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "12px 16px",
        borderRadius: 6,
        background: isCritical ? "#fef2f2" : "#fef9c3",
        border: `1px solid ${isCritical ? "#fecaca" : "#fde68a"}`,
      }}
    >
      <SeverityDot severity={alert.severity} />
      <div style={s.alertBody}>
        <span style={s.alertMessage}>{alert.message}</span>
        <span style={s.alertDetail}>
          {t("dashboard.runbook_prefix")} {alert.detail}
        </span>
      </div>
      <Link
        to={alert.link_to}
        style={{
          ...s.alertLink,
          color: isCritical ? "#dc2626" : "#854d0e",
        }}
      >
        {alert.link_label}
      </Link>
    </div>
  );
}

// ─── Main component ───

export function AlertsSection({
  alerts,
}: AlertsSectionProps): React.JSX.Element {
  if (alerts.length === 0) {
    return (
      <div style={s.healthyRow}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "#16a34a",
            display: "inline-block",
            flexShrink: 0,
          }}
        />
        <span style={s.healthyText}>{t("dashboard.all_systems_healthy")}</span>
      </div>
    );
  }

  return (
    <div style={s.alertList}>
      {alerts.map((alert) => (
        <AlertRow key={alert.id} alert={alert} />
      ))}
    </div>
  );
}

// ─── Styles ───

const s: Record<string, React.CSSProperties> = {
  alertList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  alertBody: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  alertMessage: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--color-text)",
  },
  alertDetail: {
    fontSize: 12,
    color: "var(--color-text-secondary)",
    fontStyle: "italic",
  },
  alertLink: {
    fontSize: 13,
    fontWeight: 500,
    textDecoration: "underline",
    whiteSpace: "nowrap",
    alignSelf: "flex-end",
  },
  healthyRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 16px",
    borderRadius: 6,
    background: "#dcfce7",
    border: "1px solid #bbf7d0",
  },
  healthyText: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--color-text)",
  },
};
